import { Dispatch, SetStateAction, useEffect } from "react";
import { useParams } from "react-router-dom";

import { SATOSPortal } from "@/data-adapter/SATOSPortal";
import { ArchiveUsersResponse } from "@/features/archive/model/archive/ArchiveUsersResponse";
import {
	ArchiveProject,
	ArchivedProjectsRequest,
	ArchivedProjectsResponse,
} from "@/features/archive/model/archive/ArchivedProjectsRequest";
import { ProcedureGroup } from "@/features/procedures/model/procedure/ProcedureGroup";
import { useQuery } from "@tanstack/react-query";

import { useFetchProcedureGroups } from "@/service/ProcedureQueries";
import { RouteParams, useLocalStorage } from "@/service/Utilities";

export const useFetchArchivedProcedures = (
	request: ArchivedProjectsRequest,
	options?: { enabled?: boolean },
) => {
	const { id } = useParams<RouteParams>();
	const procedureId = id != null ? parseInt(id, 10) : NaN;
	const hasValidProcedureId = id != null && !isNaN(procedureId);

	const effectiveProcedureId = hasValidProcedureId
		? procedureId
		: (request.ProcedureId ?? 0);
	const finalRequest = hasValidProcedureId
		? { ...request, ProcedureId: procedureId }
		: request;

	const queryKey = [
		"archive",
		finalRequest.StartDate,
		finalRequest.EndDate,
		finalRequest.SelectedUser,
		effectiveProcedureId,
	];

	return useQuery<ArchivedProjectsResponse, Error>({
		queryKey,
		queryFn: () => SATOSPortal.QueryArchivedProcedures(finalRequest),
		enabled: options?.enabled !== false,
	});
};

export const useFetchArchivedProcedure = () => {
	const { id } = useParams<RouteParams>();
	const procedureId = id != null ? parseInt(id, 10) : NaN;
	const hasValidId = id != null && id !== "" && !isNaN(procedureId);

	return useQuery<ArchiveProject>({
		queryKey: ["archive", "procedure", id ?? null],
		queryFn: () => SATOSPortal.QueryArchivedProcedure(id!),
		enabled: hasValidId,
	});
};

export const useFetchArchiveUsers = () => {
	return useQuery<ArchiveUsersResponse>({
		queryKey: ["archive", "users"],
		queryFn: () => SATOSPortal.QueryArchiveUsers(),
	});
};

// TODO: This should be a custom hook instead of a query - Ustin
export const useFetchArchiveProcedureGroupFolders = (): {
	groupList: ProcedureGroup[];
	setGroupList: Dispatch<SetStateAction<ProcedureGroup[]>>;
	closeAllProcedureGroupFolders: () => void;
	openAllProcedureGroupFolders: () => void;
} => {
	const [groupListLocalStorage, setGroupListLocalStorage] = useLocalStorage<
		ProcedureGroup[]
	>("archiveFoldersState", []);

	const { data: groups } = useFetchProcedureGroups("?$orderby=Name asc");

	useEffect(() => {
		if (!groupListLocalStorage || groupListLocalStorage.length === 0) {
			if (groups) {
				setGroupListLocalStorage(
					groups.sort((a, b) => sortArchiveGroupFolders(a, b)),
				);
			}
		}

		if (groups && groupListLocalStorage) {
			if (groupListLocalStorage.length !== groups.length) {
				setGroupListLocalStorage(groups);
			} else {
				const sortedGroups = groups.sort((a, b) =>
					sortArchiveGroupFolders(a, b),
				);
				for (let i = 0; i < sortedGroups.length; i++) {
					if (
						sortedGroups[i].Id !== groupListLocalStorage[i].Id ||
						sortedGroups[i].Name !== groupListLocalStorage[i].Name
					) {
						setGroupListLocalStorage(sortedGroups);
						return;
					}
				}
			}
		}
	});

	const closeAllProcedureGroupFolders = () => {
		setGroupListLocalStorage([
			...groupListLocalStorage.map((group) => {
				group.Expanded = false;
				return group;
			}),
		]);
	};

	const openAllProcedureGroupFolders = () => {
		setGroupListLocalStorage([
			...groupListLocalStorage.map((group) => {
				group.Expanded = true;
				return group;
			}),
		]);
	};

	return {
		groupList: groupListLocalStorage,
		setGroupList: setGroupListLocalStorage,
		closeAllProcedureGroupFolders,
		openAllProcedureGroupFolders,
	};
};

export const sortArchiveGroupFolders = (
	a: ProcedureGroup,
	b: ProcedureGroup,
): number => {
	if (a.SortNumber === 0 && b.SortNumber === 0) {
		return a.Name.localeCompare(b.Name);
	}

	if (a.SortNumber === 0) {
		return 1;
	}

	if (b.SortNumber === 0) {
		return -1;
	}

	return a.SortNumber - b.SortNumber;
};
